import React, { FC, useMemo } from "react";
import { IPayout } from "types";
import { Table } from "components/Table/Table";
import { formatDate, formatSum, formatSums } from "utils/format";
import styles from "./ContractPayouts.module.scss";

// Компонент для отображения таблицы расчётов
interface CalculationsTableProps {
  calculations: IPayout[];
}

const CalculationsTable: FC<CalculationsTableProps> = ({ calculations }) => {
  // Фиксированные столбцы для расчётов
  const columns = useMemo(() => [
    { title: "Дата расчёта", key: "dateR", render: (val: string) => formatDate(val), width: "200px" },
    { title: "Капитализированные %", key: "capitalizedPrc", render: (val: number) => formatSums(val), width: "200px" },
    { title: "Основной долг", key: "balanceOD", render: (val: number) => formatSums(val), width: "160px" },
    { title: "Итого", key: "cumulativeAmount", render: (val: number) => formatSums(val), width: "160px" },
  ], []);

  // Оборачиваем calculations в копию и разворачиваем её, чтобы отобразить данные с конца
  const data = useMemo(() => 
    calculations.slice().reverse().map((item, index) => ({ value: item, key: index })),
  [calculations]);

  return (
    <div className={styles.tableWrapper}>
      <Table uniqueKey="id" data={data} columns={columns} className={styles.table} />
    </div>
  );
};

// Компонент для отображения таблицы платежей
interface PaymentsTableProps {
  payments: IPayout[];
}

const PaymentsTable: FC<PaymentsTableProps> = ({ payments }) => {
  // Словарь для отображения типа выплаты (при необходимости)
  const PaymentTypeLabels: { [key: number]: string } = {
    1: "Выплата %",
    2: "Капитализированные %",
    3: "Отток ОД",
    4: "Отток Погашение ОД",
  };

  // Фиксированные столбцы для платежей
  const columns = useMemo(() => [
    { title: "Последняя дата оплаты", key: "lastPaymentDate", render: (val: string) => formatDate(val) },
    { title: "Тип выплаты", key: "typePayment", render: (val: number) => PaymentTypeLabels[val] || val },
    { title: "Кол-во дней", key: "amountDays" },
    { title: "Сумма", key: "sum", render: (val: number) => formatSums(val) },
    { title: "НДФЛ", key: "ndfl", render: (val: number) => formatSums(val) },
    { title: "К оплате", key: "toPayNdfl", render: (val: number) => formatSums(val) },
    { title: "Всего оплачено", key: "paid", render: (val: number) => formatSums(val) },
  ], []);

  const filteredPayments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 
    
    return payments.filter(payment => {
      if (!payment.lastPaymentDate) return false;
      const paymentDate = new Date(payment.lastPaymentDate);
      return paymentDate <= today;
    });
  }, [payments]);

  const data = useMemo(() => 
    filteredPayments.map((item, index) => ({ value: item, key: index })),
  [filteredPayments]);

  return (
    <div className={styles.tableWrapper}>
      <Table uniqueKey="id" data={data} columns={columns} className={styles.table} />
    </div>
  );
};

// Основной компонент, объединяющий обе таблицы
interface ContractPayoutsProps {
  calculations: IPayout[];
  payments: IPayout[];
}

export const ContractPayouts: FC<ContractPayoutsProps> = ({ calculations, payments }) => {
  return (
    <div className={styles.ContentWrapper}>
      <h2>Расчёты</h2>
      <CalculationsTable calculations={calculations} />
      <h2>Выплачено</h2>
      <PaymentsTable payments={payments} />
    </div>
  );
};
